// Last-known-good cache for a few read-only queries so the dashboard and the
// session chooser paint instantly on a fresh page load, then refresh.
//
// Keyed by the full React Query key (which includes the uid), so one account
// never sees another's data. Browser storage may be unavailable (private
// windows, blocked site data): every access is guarded and silently skipped.

import type { QueryClient } from '@tanstack/react-query';

const PREFIX = 'cnapractice.query.';
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

function storageKey(queryKey: readonly unknown[]): string {
  return PREFIX + JSON.stringify(queryKey);
}

export function readPersisted<T>(queryKey: readonly unknown[]): { data: T; at: number } | undefined {
  try {
    const raw = localStorage.getItem(storageKey(queryKey));
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as { data?: T; at?: number };
    if (!parsed || typeof parsed.at !== 'number' || parsed.data === undefined) return undefined;
    if (Date.now() - parsed.at > MAX_AGE_MS) return undefined;
    return { data: parsed.data, at: parsed.at };
  } catch {
    return undefined;
  }
}

export function writePersisted(queryKey: readonly unknown[], data: unknown): void {
  try {
    localStorage.setItem(storageKey(queryKey), JSON.stringify({ at: Date.now(), data }));
  } catch {
    // Storage full or unavailable: the live query still works.
  }
}

/** Spread into `useQuery` so the last successful result is the initial data. */
export function persistedQueryOptions<T>(queryKey: readonly unknown[]): { initialData: () => T | undefined; initialDataUpdatedAt: () => number | undefined } {
  return {
    initialData: () => readPersisted<T>(queryKey)?.data,
    initialDataUpdatedAt: () => readPersisted<T>(queryKey)?.at,
  };
}

/** Persist every successful result for queries whose first key segment is listed. */
export function persistQueryCache(queryClient: QueryClient, firstKeys: readonly string[]): () => void {
  return queryClient.getQueryCache().subscribe((event) => {
    if (event.type !== 'updated' || event.action.type !== 'success') return;
    const key = event.query.queryKey;
    if (typeof key[0] !== 'string' || !firstKeys.includes(key[0]) || key[1] == null) return;
    writePersisted(key, event.query.state.data);
  });
}
